import { isEqual, isEmpty, cloneDeep, set } from "lodash";
import moment from "moment";
import { firestore, MODEL_COLL, USERS_COLL } from "_firebase";

import types from "./types";

// import { saveChbaNzChecklist } from "../ProjectPathChecklist/_ducks/thunk";
import { actions as modelActions } from "features/Model/_ducks";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradesActions } from "features/Model/Upgrades/_ducks";
import { addTransactionToHistory } from "store/payments/thunk";
import { actions as userActions } from "store/users";
import { change } from "store/root/actions";

import { updateObject } from "utils";
import { resetDisplayUnits } from "components/Input/InputWithUnits";
import { mergeObjects, sanitizeObject } from "utils/objects";
import cleanComponents from "utils/cleanComponents";
import { calcWindowToWallRatio } from "utils/enclosure/components";
import { removeUndefined } from "utils/generalUtils/generalUtils";
import { runEnergyPlusAPI } from "utils/review/api";
import { settleRxRResultsUnlock as settleRxRResultsUnlockAPI } from "utils/payment/api";

const {
    updateHouseModel,
    addMissingCodeWarning,
    appendToChbaAdminCommunity,
    runEnergyPlusStart,
    runEnergyPlusSuccess,
    activateInsightsStart,
    activateInsightsSuccess,
    activateInsightsError,
    unlockRxRResultsStart,
    unlockRxRResultsSuccess,
    unlockRxRResultsError,
} = modelActions;
const { clearEnclosureState } = enclosureActions;
const { saveUpgrades } = upgradesActions;

const ENERGY_PLUS_RESULTS_COLLECTION = "energyPlusResults";
const ENERGY_PLUS_DOC_META_DATA = "metaData";
const ENERGY_PLUS_DOC_LOGS = "logs";
const ENERGY_PLUS_DOC_HPXML = "hpxml";
const ENERGY_PLUS_DOC_USAGE_DATA = "usageData";
const ENERGY_PLUS_DOC_LOADS_TEMPS_DATA = "loadsTempsData";
const ENERGY_PLUS_DOC_ANNUAL_RESULTS = "annualResults";

const hasAnnualResults = (annualResults) => {
    if (!annualResults) return false;
    if (Array.isArray(annualResults)) return annualResults.length > 0;
    if (typeof annualResults === "object") return Object.keys(annualResults).length > 0;
    return Boolean(annualResults);
};

const getEnergyPlusResultsCollectionRef = (modelId) =>
    firestore.doc(`${MODEL_COLL}/${modelId}`).collection(ENERGY_PLUS_RESULTS_COLLECTION);

export const fetchEnergyPlusResults = (modelId) => async (dispatch) => {
    if (!modelId) return false;

    const collectionRef = getEnergyPlusResultsCollectionRef(modelId);

    // We consider "any present" to mean metaData or annualResults exists.
    const [metaSnap, annualSnap] = await Promise.all([
        collectionRef.doc(ENERGY_PLUS_DOC_META_DATA).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_ANNUAL_RESULTS).get(),
    ]);

    if (!metaSnap.exists && !annualSnap.exists) {
        // Keep redux/state consistent with the absence of data.
        dispatch(runEnergyPlusSuccess({}));
        return false;
    }

    const [metaDataSnap, logsSnap, hpxmlSnap, usageSnap, loadsTempsSnap, annualResultsDocSnap] = await Promise.all([
        collectionRef.doc(ENERGY_PLUS_DOC_META_DATA).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_LOGS).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_HPXML).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_USAGE_DATA).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_LOADS_TEMPS_DATA).get(),
        collectionRef.doc(ENERGY_PLUS_DOC_ANNUAL_RESULTS).get(),
    ]);

    const metaData = metaDataSnap.exists ? metaDataSnap.data() || {} : {};
    const dataMeta = metaData?.data && typeof metaData.data === "object" ? metaData.data : {};

    const usageData = usageSnap.exists ? usageSnap.data()?.usage_data || {} : {};
    const loadsTempsData = loadsTempsSnap.exists ? loadsTempsSnap.data()?.loads_temps_data || {} : {};
    const annualResults = annualResultsDocSnap.exists ? annualResultsDocSnap.data()?.annual_results || {} : {};

    const errors = logsSnap.exists ? logsSnap.data()?.errors || [] : [];
    const warnings = logsSnap.exists ? logsSnap.data()?.warnings || [] : [];

    const hpxml = hpxmlSnap.exists ? (hpxmlSnap.data()?.hpxml ?? null) : null;

    const nextEnergyPlusResults = {
        data: {
            ...dataMeta,
            usage_data: usageData,
            loads_temps_data: loadsTempsData,
            annual_results: annualResults,
        },
        logs: {
            errors,
            warnings,
        },
        hpxml,
        metaData,
    };

    dispatch(runEnergyPlusSuccess(nextEnergyPlusResults));
    return true;
};

export const runEnergyPlus =
    (modelId, expVersion, upgradePackage = {}) =>
    async (dispatch, getState) => {
        dispatch(runEnergyPlusStart());

        if (!modelId) return { ok: false };

        const { user: { uid: userId = "" } = {} } = getState();
        try {
            const { data = {} } = await runEnergyPlusAPI(modelId, expVersion, upgradePackage);
            const energyPlusResultsFromApi = data || {};

            const apiData = energyPlusResultsFromApi?.data || {};
            const apiLogs = energyPlusResultsFromApi?.logs || {};
            const hpxml = energyPlusResultsFromApi?.hpxml ?? null;
            const jobId = energyPlusResultsFromApi?.job_id ?? apiData?.job_id ?? null;

            const { usage_data = {}, loads_temps_data = {}, annual_results = {}, ...dataMeta } = apiData;
            const errors = apiLogs?.errors || [];
            const warnings = apiLogs?.warnings || [];

            // If the run "failed" (no annual results), do not overwrite Firestore documents.
            if (!hasAnnualResults(annual_results)) {
                // Ensure UI shows whatever is currently in Firestore (last successful run).
                await dispatch(fetchEnergyPlusResults(modelId));
                // Overlay logs from this API response so the user can see why the run failed.
                const { model: { energyPlusResults: merged = {} } = {} } = getState();
                dispatch(
                    runEnergyPlusSuccess({
                        ...merged,
                        logs: {
                            errors,
                            warnings,
                        },
                    })
                );
                return { ok: false };
            }

            const metaDataDoc = {
                data: {
                    ...dataMeta,
                },
                simulation: {
                    simulationTime: moment().format("LLLL"),
                    upgradePackage, // {upgradePackageId, label}
                    jobId,
                    h2kVersion: expVersion,
                    userId,
                },
            };

            const collectionRef = getEnergyPlusResultsCollectionRef(modelId);
            const batch = firestore.batch();

            // Overwrite with fixed document IDs.
            batch.set(collectionRef.doc(ENERGY_PLUS_DOC_META_DATA), metaDataDoc);
            batch.set(
                collectionRef.doc(ENERGY_PLUS_DOC_LOGS),
                {
                    errors,
                    warnings,
                },
                { merge: false }
            );
            batch.set(collectionRef.doc(ENERGY_PLUS_DOC_HPXML), { hpxml }, { merge: false });
            batch.set(collectionRef.doc(ENERGY_PLUS_DOC_USAGE_DATA), { usage_data }, { merge: false });
            batch.set(collectionRef.doc(ENERGY_PLUS_DOC_LOADS_TEMPS_DATA), { loads_temps_data }, { merge: false });
            batch.set(collectionRef.doc(ENERGY_PLUS_DOC_ANNUAL_RESULTS), { annual_results }, { merge: false });

            await batch.commit();

            dispatch(
                runEnergyPlusSuccess({
                    ...energyPlusResultsFromApi,
                    data: {
                        ...dataMeta,
                        usage_data,
                        loads_temps_data,
                        annual_results,
                    },
                    logs: {
                        errors,
                        warnings,
                    },
                    hpxml,
                    metaData: metaDataDoc,
                })
            );

            return { ok: true };
        } catch (error) {
            console.log("error", error?.message);
            // On a hard error, do not overwrite Firestore; show previous.
            await dispatch(fetchEnergyPlusResults(modelId));
            return { ok: false, error };
        }
    };

// Switching the model's unit system also clears every field's stored display unit so
// fields that mount later fall back to the new system's default instead of the old one
export const setPrimaryUnits = (primaryUnits) => (dispatch, getState) => {
    const { model: { modelData = {} } = {} } = getState();

    if (isEmpty(modelData)) return;

    const updatedModelData = resetDisplayUnits(modelData);
    set(updatedModelData, "uiSettings.primaryUnits", primaryUnits);

    dispatch(change("model.modelData", updatedModelData));
};

export const filterCodes = ({ stashedCodes = {}, modelCodes = {}, codeLib = {} }) =>
    Object.values(stashedCodes)
        .filter((value) => value && !modelCodes[value])
        .reduce((prev, current) => {
            const { componentType, codeType, ...rest } = codeLib[current] || {};

            return {
                ...prev,
                [current]: {
                    codeRef: current,
                    ...rest,
                },
            };
        }, {});

// export const cleanCodes = (codesObj) =>
//     Object.keys(codesObj)
//         .filter((key) => codesObj[key].label != null)
//         .reduce((prev, current) => ({ ...prev, [current]: codesObj[current] }), {});

export const cleanCodes = (codesObj) =>
    Object.fromEntries(
        Object.entries(codesObj)
            .map(([key, value]) => {
                // If key is empty but value.codeRef exists, use that
                const fixedKey = key === "" && value.codeRef ? value.codeRef : key;
                return [fixedKey, value];
            })
            .filter(([key, value]) => key && value?.label != null && value.label !== "")
    );

// ***** SAVING MODEL HERE ****** //
export const saveModel =
    (modelId, setSubmitting, isFromError = false) =>
    async (dispatch, getState) => {
        const {
            model: {
                modelData: latestFormModelData = {},
                modelDetails: latestFormModelDetails = {},
                modelTakeoff: latestModelTakeoff = {},
                stashedCodes = {},
                lastSnapshot = {},
                modelDetailsToCompare = {},
                modelDataToCompare = {},
                modelTakeoffToCompare = {},
                dataEntryMode = "combined",
                linkedBaseline = {},
            } = {},
            user: { name: username = "", uid = "", codeLib = {} } = {},
        } = getState();

        const { owner = {}, writePermission = [] } = latestFormModelDetails;
        const { uid: ownerId = "" } = owner;

        const readOnly = ownerId !== uid && !writePermission.includes(uid);

        if (readOnly) {
            // console.log("not owner, skipped save");
            return;
        }

        setSubmitting(true);

        let modelData = isFromError
            ? sanitizeObject(cloneDeep(latestFormModelData), modelDataToCompare)
            : cloneDeep(latestFormModelData);
        let modelDetails = isFromError
            ? sanitizeObject(cloneDeep(latestFormModelDetails), modelDetailsToCompare)
            : cloneDeep(latestFormModelDetails);
        let modelTakeoff = isFromError
            ? sanitizeObject(cloneDeep(latestModelTakeoff), modelTakeoffToCompare)
            : cloneDeep(latestModelTakeoff);

        // if (isFromError) {
        //     console.log("FROM ERROR");
        //     console.log(latestModelTakeoff, modelTakeoff);

        //     return;
        // }

        const {
            modelDetails: modelDetailsFromSnapshot,
            modelData: modelDataFromSnapshot,
            modelTakeoff: modelTakeoffFromSnapshot,
        } = lastSnapshot;

        if (!isFromError) dispatch({ type: types.UPDATE_MODEL_SNAPSHOT_START });

        const lastEdited = {
            name: username,
            uid: uid,
            datetime: moment().format(),
        };

        if (!isEqual({ ...modelDetailsFromSnapshot, lastEdited: {} }, { ...modelDetailsToCompare, lastEdited: {} })) {
            modelDetails = mergeObjects(latestFormModelDetails, modelDetailsToCompare, modelDetailsFromSnapshot);
        }

        //modelDataToCompare
        if (!isEqual(modelDataFromSnapshot, modelDataToCompare)) {
            modelData = mergeObjects(latestFormModelData, modelDataToCompare, modelDataFromSnapshot);
        }

        if (!isEqual(modelTakeoffToCompare, modelTakeoffFromSnapshot)) {
            modelTakeoff = mergeObjects(latestModelTakeoff, modelTakeoffToCompare, modelTakeoffFromSnapshot);
        }

        const { codes = {}, components = {} } = modelData;

        //Filter and map stashed codes to get library-only codes
        //Iterating over component identifiers
        const libCodes = Object.keys(stashedCodes)
            .filter((component) => codeLib[component] && codeLib[component].codes)
            .map((component) =>
                filterCodes({
                    stashedCodes: stashedCodes[component],
                    modelCodes: codes,
                    codeLib: codeLib[component].codes,
                })
            )
            .reduce(
                (prev, current) => ({
                    ...prev,
                    ...current,
                }),
                {}
            );

        // Run cleanup on components
        // const cleanedComps = cleanComponents(components);
        const { cleanedComps, codesInModel = {} } = cleanComponents(components, "codeCheck");

        const windowToWallValue = modelData?.dimensions?.windowToWall?.value || 0;
        const windowToWallRatio = calcWindowToWallRatio(components) || 0;

        const newWindowToWallTrimmed = parseFloat((100 * windowToWallRatio).toFixed(3));

        //Only update if a change has happened
        if (newWindowToWallTrimmed !== windowToWallValue && !Number.isNaN(newWindowToWallTrimmed)) {
            // modelData.dimensions.windowToWall.value = newWindowToWallTrimmed;
            modelData = {
                ...modelData,
                dimensions: {
                    ...modelData.dimensions,
                    windowToWall: {
                        ...modelData.dimensions.windowToWall,
                        value: newWindowToWallTrimmed,
                    },
                },
            };
        }

        const modelDataForSave = { ...modelData };

        //Add library codes and cleaned components to model

        const updatedCodes = cleanCodes(updateObject(cleanCodes(codes), libCodes));

        const formData = removeUndefined(
            updateObject(getState().model, {
                modelData: updateObject(modelData, {
                    codes: updatedCodes,
                    components: cleanedComps,
                }),
                modelDetails: updateObject(modelDetails, {
                    lastEdited: lastEdited,
                }),
                modelTakeoff,
                dataEntryMode,
                linkedBaseline,
            })
        );

        //Console logs for identifying missing code issue
        let missingCodes = {};
        Object.keys(codesInModel).forEach((codeRefInModel) => {
            if (!Object.keys(formData.modelData.codes).includes(codeRefInModel)) {
                console.log(
                    "A code reference was found in the model that was not saved properly to the form: ",
                    codeRefInModel
                );
                // console.log("Codes in form: ", formData.modelData.codes);

                missingCodes[codeRefInModel] = codesInModel[codeRefInModel];
            }

            if (!Object.keys(updatedCodes).includes(codeRefInModel)) {
                console.log(
                    "A code reference was found in the model that was not present after the codes were cleaed: ",
                    codeRefInModel
                );
                // console.log("Codes after clean: ", updatedCodes);
                // console.log("Codes before clean: ", codes);
                // console.log("libCodes before merge: ", libCodes);

                missingCodes[codeRefInModel] = codesInModel[codeRefInModel];
            }
        });

        if (!isEmpty(missingCodes)) {
            dispatch(addMissingCodeWarning(missingCodes));
        }

        //CHECK FOR CHBA TAG AND APPEND TO COMMUNITY IF REQUIRED
        const { tags = [] } = modelDetails;
        //Check if the file's information needs to be updated in the community
        if (tags.some((tag) => tag.includes("CHBA_NZ")) && !isFromError) {
            await dispatch(appendToChbaAdminCommunity(modelId, uid));
        }
        const tasks = [];

        if (!isFromError) {
            tasks.push(dispatch(clearEnclosureState()));
        }

        tasks.push(dispatch(saveUpgrades({ ...codeLib, ...codes }, isFromError)));
        // tasks.push(dispatch(saveChbaNzChecklist(isFromError)));

        tasks.push(dispatch(updateHouseModel(formData, modelId, uid, isFromError)));

        return Promise.all(tasks).then(() => {
            if (!isFromError) setSubmitting(false);
        });
    };

export const activateInsights = (modelId, modelName) => async (dispatch, getState) => {
    dispatch(activateInsightsStart());

    try {
        const {
            user: { uid: userId = "", creditSummary: { credits = 0 } = {} } = {},
            model: { modelDetails: { isInsightsActive = false } = {} } = {},
        } = getState();

        if (!userId) {
            throw new Error("User not found");
        }

        if (isInsightsActive) {
            dispatch(activateInsightsSuccess());
            return;
        }

        if (credits < 1) {
            throw new Error("Insufficient credits");
        }

        // Backend atomically debits the credit and sets isInsightsActive / isRxRResultsUnlocked
        // on the model doc and modelDir. Do not write those flags from the client —
        // Firestore rules block it and it was a free-unlock risk.
        await dispatch(
            addTransactionToHistory({
                type: "SPENT.INSIGHTS",
                credits: 1,
                spentDetails: {
                    feature: "INSIGHTS",
                    url: "/model",
                    id: modelId,
                    name: modelName,
                },
            })
        );

        // Optimistic local modelDir update so the dashboard icon refreshes immediately.
        dispatch(userActions.updateModelInDir(modelId, { isInsightsActive: true }));
        dispatch(activateInsightsSuccess());
    } catch (error) {
        console.log("error", error);
        dispatch(activateInsightsError(error));
        throw error;
    }
};

export const unlockRxRResults = (modelId) => async (dispatch) => {
    dispatch(unlockRxRResultsStart());

    try {
        if (!modelId) {
            throw new Error("Model ID is required");
        }

        // Backend evaluates Insights / pre-launch grandfathering and persists the flag.
        // Do not write isRxRResultsUnlocked from the client — Firestore rules block it.
        const response = await settleRxRResultsUnlockAPI({ modelId });

        if (!response?.data || response?.isAxiosError) {
            const message =
                response?.response?.data?.message || response?.message || "Failed to settle RxR results unlock";
            throw new Error(message);
        }

        const { isRxRResultsUnlocked = false } = response.data;

        dispatch(unlockRxRResultsSuccess(isRxRResultsUnlocked));
    } catch (error) {
        console.log("error", error);
        dispatch(unlockRxRResultsError(error));
    }
};
